import Phaser from 'phaser'
import { GameState, createInitialGameState } from '../types'
import { applyClick } from '../systems/tokenSystem'
import { applyPassiveGeneration, purchaseBuilding } from '../systems/buildingSystem'
import { purchaseUpgrade } from '../systems/upgradeSystem'
import { isBuildingUnlocked } from '../systems/visibilitySystem'
import { loadOrCreateState, saveToLocalStorage, clearSave } from '../systems/persistenceSystem'
import { UIManager } from '../ui/UIManager'
import { GameEvents, GameEventPayloads } from '../events/GameEvents'

const AUTO_SAVE_INTERVAL_MS = 30000

export class GameScene extends Phaser.Scene {
  private gameState: GameState = createInitialGameState()
  private uiManager: UIManager | null = null

  constructor() {
    super({ key: 'GameScene' })
  }

  create(): void {
    this.gameState = loadOrCreateState()
    this.createUI()
    this.setupEventListeners()
    this.setupAutoSave()
  }

  update(): void {
    const currentTimeMs = Date.now()
    this.gameState = applyPassiveGeneration(this.gameState, currentTimeMs)

    if (this.uiManager !== null) {
      this.uiManager.update(this.gameState)
    }
  }

  private createUI(): void {
    this.uiManager = new UIManager(this)
    this.uiManager.create()
  }

  private setupEventListeners(): void {
    this.game.events.on(GameEvents.CLICK, this.handleClick, this)
    this.game.events.on(GameEvents.BUILDING_PURCHASE, this.handleBuildingPurchase, this)
    this.game.events.on(GameEvents.UPGRADE_PURCHASE, this.handleUpgradePurchase, this)
    this.game.events.on(GameEvents.RESET, this.handleReset, this)
  }

  private handleClick(): void {
    this.gameState = applyClick(this.gameState)
  }

  private handleBuildingPurchase(payload: GameEventPayloads[typeof GameEvents.BUILDING_PURCHASE]): void {
    const { buildingId } = payload

    if (!isBuildingUnlocked(this.gameState, buildingId)) {
      return
    }

    const result = purchaseBuilding(this.gameState, buildingId)
    if (result.isOk()) {
      this.gameState = result.value
      this.saveGame()
      this.uiManager?.refreshAllTooltips(this.gameState)
    }
  }

  private handleUpgradePurchase(payload: GameEventPayloads[typeof GameEvents.UPGRADE_PURCHASE]): void {
    const { upgradeId } = payload

    const result = purchaseUpgrade(this.gameState, upgradeId)
    if (result.isOk()) {
      this.gameState = result.value
      this.saveGame()
      this.uiManager?.refreshAllTooltips(this.gameState)
    }
  }

  private handleReset(): void {
    clearSave()
    this.gameState = createInitialGameState()
  }

  private setupAutoSave(): void {
    this.time.addEvent({
      delay: AUTO_SAVE_INTERVAL_MS,
      callback: () => {
        this.saveGame()
      },
      loop: true,
    })
  }

  private saveGame(): void {
    saveToLocalStorage(this.gameState)
  }
}
